import { Fragment, useEffect, useMemo, useState } from 'react';
import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { CommPreferenceV1 } from '@frontend/api-comm-preference';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { NoAccessEmptyState } from '@frontend/assets';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { convertV3CalendarEventsToV2Appointments, useLocationTimezone } from '@frontend/schedule-helpers';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import {
  Accordion,
  Button,
  Checkbox,
  ChecklistField,
  Chip,
  ContentLoader,
  Heading,
  NakedUl,
  NotificationBadge,
  Text,
  useControlledField,
  useFormField,
} from '@frontend/design-system';
import { customAccordionStyles } from '../../../../../components/location-accordion-list';
import { MINUTE_IN_MILLISECONDS } from '../../../../../constants';
import { useScheduleActionsContainerMethod } from '../../../../../context/ScheduleActionsContainerContext';
import { useGetCalendarV3QueriesData } from '../../../../../hooks';
import { useCalendarStore } from '../../../../../stores';
import { CalendarHeader } from '../../../../CalendarV3/components/header';
import { PatientListRow } from '../../../components/PatientListRow';
import { ScheduleMassMessageStepEnum } from '../../../types';
import { SendScheduledMessageToPatients } from '../ScheduleMassMessages/SendScheduledMessageToPatients';
import { ScheduleMassMessageTrackingIds } from '../ScheduleMassMessages/trackingIds';

const trackingIds = ScheduleMassMessageTrackingIds;

export const SchedulePulseContactPatientsPanelV3 = () => {
  const { t } = useTranslation('scheduleCalendarActions');
  const { getLocationTimezone } = useLocationTimezone();
  const { featureFlags } = useAppFlagStore();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const { setHideTabs, selectedLocationId, closeModal } = useScheduleActionsContainerMethod();
  const timeZone = getLocationTimezone(selectedLocationId ?? '');
  const [currStep, setCurrStep] = useState(ScheduleMassMessageStepEnum.SELECT_PATIENTS_TO_CONTACT);

  const inboxACLEnforcementEnabled = featureFlags[selectedLocationId ?? '']?.get(
    'nwx:messaging-inbox-fe-acl-enforcement'
  )?.value;
  const showMessagesNoAccessState = inboxACLEnforcementEnabled
    ? !hasSchemaACL(selectedLocationId ?? '', Permission.SMS_INBOX_MANAGE)
    : false;

  const { calendarDate } = useCalendarStore(['calendarDate']);
  const { filteredCalendarEventsData, calendarCounts, calendarTodayDate, isLoadingCalendarEvents } =
    useGetCalendarV3QueriesData();

  const commPreferenceQuery = CommPreferenceV1.Queries.useListByPreferenceQuery({
    request: {
      locationId: selectedLocationId ?? '',
      channel: Channel.CHANNEL_SMS,
      messageType: MessageType_Enum.APPOINTMENT_MANUAL_REMINDER,
    },
    options: {
      enabled: !!selectedLocationId,
      /*
          NOTE: `staleTime` of 5 minutes is added here because the query is not invalidated when user changes their sms preference
          through link in Contact Profile and there is no backend API restriction on sending the message to opted out numbers.
          This should be removed once we have backend capability to restrict sending messages to opted out numbers.
        */
      staleTime: 5 * MINUTE_IN_MILLISECONDS,
    },
  });

  const appointments = useMemo(() => {
    const filteredEvents = filteredCalendarEventsData.filter((event) => event.locationId === selectedLocationId);

    const appointments = convertV3CalendarEventsToV2Appointments(filteredEvents, locationTimezoneMap);
    return appointments;
  }, [filteredCalendarEventsData, locationTimezoneMap, selectedLocationId]);

  const { optInAppointments, optOutAppointments, startingValue } = useMemo(() => {
    const optInAppointments: Appointment[] = [];
    const optOutAppointments: Appointment[] = [];

    const optedOutNumbers = commPreferenceQuery.data?.optedOut?.map((number) => formatPhoneNumber(number)) ?? [];

    appointments.map((appointment) => {
      const mobilePhone = formatPhoneNumber(appointment.person?.mobilePhone);
      if (mobilePhone && optedOutNumbers.includes(mobilePhone)) {
        optOutAppointments.push(appointment);
      } else {
        optInAppointments.push(appointment);
      }
    });

    const startingValue = optInAppointments?.length
      ? 'message-list'
      : optOutAppointments?.length
      ? 'phone-call-list'
      : 'first';

    return { optInAppointments, optOutAppointments, startingValue };
  }, [appointments, commPreferenceQuery.data?.optedOut]);

  const [selectedAppointmentIds, setSelectedAppointmentIds] = useState<string[]>([]);
  const checklistFieldProps = useFormField(
    {
      type: 'checklist',
      value: selectedAppointmentIds,
    },
    [selectedAppointmentIds]
  );
  const selectAllProps = useControlledField({
    type: 'checkbox',
    value: !!optInAppointments.length && checklistFieldProps.value.length === optInAppointments?.length,
    onChange: () => {
      if (checklistFieldProps.value.length === optInAppointments?.length) {
        setSelectedAppointmentIds([]);
      } else {
        setSelectedAppointmentIds(optInAppointments?.map((appt) => appt.id ?? '') ?? []);
      }
    },
  });

  useEffect(() => {
    setSelectedAppointmentIds([]);
  }, [calendarDate]);

  const selectedAppointments = useMemo(() => {
    return (
      checklistFieldProps.value?.map((apptId) => {
        return optInAppointments?.find((appt) => appt.id === apptId) ?? {};
      }) ?? []
    );
  }, [checklistFieldProps.value, optInAppointments]);

  const appointmentsLocationId = useMemo(() => {
    if (!!selectedAppointments.length) {
      return selectedAppointments.find((appt) => appt.locationId)?.locationId ?? '';
    }
    return '';
  }, [selectedAppointments]);

  if (currStep === ScheduleMassMessageStepEnum.SEND_MASS_MESSAGE) {
    return (
      <SendScheduledMessageToPatients
        selectedAppointments={selectedAppointments}
        date={calendarDate}
        onClose={() => closeModal?.()}
        onBack={() => setCurrStep(ScheduleMassMessageStepEnum.SELECT_PATIENTS_TO_CONTACT)}
        appointmentLocationId={selectedLocationId ?? appointmentsLocationId ?? ''}
      />
    );
  }

  return (
    <section css={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <header css={{ zIndex: 1, boxShadow: theme.shadows.light }}>
        <Heading level={3} css={{ padding: theme.spacing(1, 3) }} color='light'>
          {t('Select Patients to Contact')}
        </Heading>
        <CalendarHeader
          calendarTodayDate={calendarTodayDate}
          calendarEventsCount={calendarCounts.total}
          unconfirmedCalendarEventsCount={calendarCounts.unconfirmed}
          insuranceAlertCalendarEventsCount={calendarCounts.insurance}
        />
      </header>
      <div css={{ position: 'relative', background: theme.colors.neutral5, flex: 1, overflowY: 'auto' }}>
        <ContentLoader size='medium' show={isLoadingCalendarEvents || commPreferenceQuery.isLoading} />
        <Accordion
          distance={3}
          css={[
            customAccordionStyles.accordionContainer,
            {
              background: theme.colors.neutral5,
              padding: theme.spacing(2, 4, 3, 4),
            },
          ]}
          showBoxShadow
          variant='location'
          chevronSize={16}
          size='small'
          startingValue={startingValue}
        >
          <Accordion.Item
            trackingId={trackingIds.messageListAccordion}
            key='message-list'
            value='message-list'
            css={{ minHeight: 40 }}
          >
            <Accordion.Header css={customAccordionStyles.header}>
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <Accordion.Header.Icon icon='messaging' title={t('Message List')} />
                <Chip.TimeZone variant='neutral'>{timeZone}</Chip.TimeZone>
              </div>
              {!!optInAppointments?.length && !showMessagesNoAccessState && (
                <NotificationBadge truncateCount css={{ backgroundColor: theme.colors.primary50 }}>
                  {optInAppointments.length}
                </NotificationBadge>
              )}
            </Accordion.Header>

            <Accordion.Body css={customAccordionStyles.body}>
              {showMessagesNoAccessState ? (
                <div css={{ padding: theme.spacing(2, 4) }}>
                  <NoAccessEmptyState message={t('You do not have permission to send text messages')} />
                </div>
              ) : (
                <>
                  {!!optInAppointments?.length && (
                    <div
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                        padding: theme.spacing(1.5, 2),
                      }}
                    >
                      <Checkbox
                        data-trackingid={ScheduleMassMessageTrackingIds.selectAllMessageListCheckbox}
                        name='select-all-rows'
                        {...selectAllProps}
                        error={false}
                        disabled={!optInAppointments?.length}
                      />
                      <Heading level={3}>{t('{{count}} Patients', { count: optInAppointments?.length })}</Heading>
                    </div>
                  )}
                  <hr css={{ border: 'none', borderBottom: `1px solid ${theme.colors.neutral20}` }} />
                  <section css={{ overflow: 'auto' }}>
                    {!!optInAppointments?.length ? (
                      <ChecklistField {...checklistFieldProps} name='message-list-checklist' label='' showLabel={false}>
                        {optInAppointments?.map((appt) => (
                          <ChecklistField.Option
                            key={appt.id ?? ''}
                            name={appt.id ?? ''}
                            css={{
                              marginBottom: 0,
                              borderBottom: `1px solid ${theme.colors.neutral20}`,
                              alignItems: 'center',
                              paddingLeft: theme.spacing(2),
                              label: {
                                flex: 1,
                              },
                            }}
                          >
                            <PatientListRow key={appt.id} appt={appt} locationId={appt?.locationId || ''} />
                          </ChecklistField.Option>
                        ))}
                      </ChecklistField>
                    ) : (
                      <Text css={{ padding: theme.spacing(2) }} color='light' size='medium'>
                        {t(
                          "No patients can be reached by text. They've either opted out of texting or don't have a textable number saved. Please contact them by phone instead."
                        )}
                      </Text>
                    )}
                  </section>
                </>
              )}
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item
            trackingId={trackingIds.phoneListAccordion}
            key='phone-call-list'
            value='phone-call-list'
            css={{ minHeight: 40 }}
          >
            <Accordion.Header css={customAccordionStyles.header}>
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <Accordion.Header.Icon icon='phone-outgoing' title={t('Phone Call List')} />
                <Chip.TimeZone variant='neutral'>{timeZone}</Chip.TimeZone>
              </div>
              {!!optOutAppointments?.length && (
                <NotificationBadge truncateCount css={{ backgroundColor: theme.colors.primary50 }}>
                  {optOutAppointments.length}
                </NotificationBadge>
              )}
            </Accordion.Header>

            <Accordion.Body css={customAccordionStyles.body}>
              <Text css={{ padding: theme.spacing(1, 2) }} color='light' size='medium'>
                {optOutAppointments.length > 0
                  ? t(
                      "The patients listed below either opted out of texting or don't have a textable number. You can still reach them by phone."
                    )
                  : optInAppointments.length > 0
                  ? t(
                      "All patients who can be contacted have been added to the Message List. If a patient doesn't appear on the list, check that their profile includes a valid phone number."
                    )
                  : t('There are no patients with a valid phone number saved in their profile.')}
              </Text>
              {!!optOutAppointments.length && (
                <>
                  <hr />
                  <NakedUl css={{ overflow: 'auto' }}>
                    {optOutAppointments?.map((appt) => {
                      return (
                        <Fragment key={appt.id}>
                          <PatientListRow optOutText appt={appt} locationId={appt?.locationId || ''} />
                          <hr css={{ border: 'none', borderBottom: `1px solid ${theme.colors.neutral20}` }} />
                        </Fragment>
                      );
                    })}
                  </NakedUl>
                </>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
      <footer
        css={{ padding: theme.spacing(2, 3), display: 'flex', gap: theme.spacing(1), justifyContent: 'flex-end' }}
      >
        <Button
          variant='tertiary'
          onClick={() => setHideTabs(false)}
          trackingId={trackingIds.backBtn}
          iconName='back'
          css={{ marginRight: 'auto' }}
        >
          {t('Back')}
        </Button>
        <Button variant='secondary' trackingId={trackingIds.cancelBtn} onClick={closeModal}>
          {t('Cancel')}
        </Button>
        <Button
          disabled={selectedAppointments.length === 0}
          onClick={() => setCurrStep(ScheduleMassMessageStepEnum.SEND_MASS_MESSAGE)}
          trackingId={trackingIds.nextBtn}
        >
          {t('Next')}
        </Button>
      </footer>
    </section>
  );
};
